// libs
import React, { useMemo } from 'react';
import capitalize from 'lodash/capitalize';

import { useTranslation } from '@lyearn/i18n';
import { CounterPill, Stack } from '@lyearn/ui';
import { Typography } from '@lyearn/ui';
// components
import * as ContentList from '@/components/ContentList';
import { useAppConfig } from '@/hooks/useAppConfig';
import ContentsDialog from '@/modules/corporate/components/ContentsDialog';
import CollectionItemViewer from '@/modules/corporate/components/ContentsDialog/CollectionItemViewer';
import { getAssignmentTypeMap } from '@/modules/corporate/components/ContentsDialog/hooks/useSessionSelection';
import SessionItemViewer from '@/modules/corporate/components/ContentsDialog/SessionItemViewer';
import { ContentsDialogProps, Tabs } from '@/modules/corporate/components/ContentsDialog/types';
import ContentItemContainer from '@/modules/corporate/modules/path/views/PathEditor/components/ContentItemContainer';
// types
import {
  ContentCollectionGroupType,
  ContentQueryIntentEnum,
  EventStatusEnum,
  EventUnion,
  GroupQueryIntentEnum,
  RecurringTypeEnum,
} from '@/types/schema';

import {
  RenderCollectionActions,
  RenderContentActions,
  RenderSessionActions,
} from '../../renderer';
import SetDueDateDialog from '../SetDueDateDialog';
import { useContentAndCollectionList } from './hooks/useContentAndCollectionList';
import { ContentListProps } from './types';

const TAB_CONFIG = [Tabs.Content, Tabs.Sessions, Tabs.Collections];

const RECURRING_CATEGORIES = [RecurringTypeEnum.NonRecurring, RecurringTypeEnum.Recurring];

const EVENT_STATUS = [
  EventStatusEnum.Scheduled,
  EventStatusEnum.Waiting,
  EventStatusEnum.Completed,
];

const DEFAULT_DIALOG_CONFIG: Partial<ContentsDialogProps['dialogConfigs']> = {
  tabConfig: TAB_CONFIG,
  recurringCategories: RECURRING_CATEGORIES,
  eventStatus: EVENT_STATUS,
};

export const ASSIGNMENT_RESTRICTION_DIALOG_CONFIG: Partial<ContentsDialogProps['dialogConfigs']> = {
  contentQueryIntent: ContentQueryIntentEnum.Assign,
  groupQueryIntent: GroupQueryIntentEnum.Assign,
};

const DIALOG_ACTIONS = { isAssingmentTypeChangeAllowed: true };

function ContentAndCollectionList(props: ContentListProps) {
  const { t } = useTranslation();
  const { contentIds, collectionIds, eventSelections, disabled } = props;
  const { site } = useAppConfig();
  const { enableAssignmentRestriction } = site;

  const { state, methods } = useContentAndCollectionList(props);
  const {
    groups,
    contents,
    contentListItems,
    openContentsDialog,
    collectionListItems,
    inViewItemForDueDate,
    assignmentItemsDueDateConfig,
    nestedDialogState,
    sessions,
    sessionListItems,
  } = state;
  const {
    onAddContent,
    onResetDueDate,
    onDeleteContent,
    onDeleteCollection,
    toggleContentsDialog,
    onCloseDueDateDialog,
    onSetDueDateForContent,
    onSetDueDateForSection,
    onSetDueDateForCollection,
    onSetDueDateForSession,
    setAssignmentItemsDueDate,
    setNestedDialogState,
    onDeleteSession,
  } = methods;

  const assignmentTypeMap = useMemo(() => getAssignmentTypeMap(eventSelections), [eventSelections]);

  return (
    <Stack direction="col">
      <Stack alignItems="center" className="mb-20" justifyContent="between">
        <Typography variant="heading-s">{t('AssignmentCreator:ContentSectionTitle')}</Typography>
        <CounterPill
          appearance="secondary"
          label={contentIds.length + collectionIds.length + eventSelections.length}
        />
      </Stack>
      <ContentList.Provider items={sessionListItems}>
        <ContentList.Items
          renderer={(index) => {
            const session = sessions[index] as EventUnion;

            return (
              <SessionItemViewer
                key={session.id}
                Actions={RenderSessionActions({
                  t,
                  disabled,
                  onDelete: () => onDeleteSession(session.id),
                  onSetDueDate: onSetDueDateForSession,
                  item: session,
                  assignmentItemsDueDateConfig,
                })}
                assignmentType={assignmentTypeMap[session.id]}
                disabled={disabled}
                event={session}
                onDelete={onDeleteSession}
              />
            );
          }}
        />
      </ContentList.Provider>
      <ContentList.Provider items={contentListItems}>
        <ContentList.Items
          renderer={(index) => {
            const content = contents[index];

            return (
              <ContentItemContainer
                key={content.id}
                Actions={
                  <RenderContentActions
                    assignmentItemsDueDateConfig={assignmentItemsDueDateConfig}
                    disabled={disabled}
                    item={content}
                    setNestedDialogState={setNestedDialogState}
                    t={t}
                    onDelete={() => onDeleteContent(content.id)}
                    onSetDueDate={onSetDueDateForContent}
                  />
                }
                content={content}
                disabled={disabled}
              />
            );
          }}
        />
      </ContentList.Provider>
      {contentListItems.length && collectionListItems.length ? (
        <Stack className="mx-4 -mb-1 border-b border-solid border-interactive-border" />
      ) : null}
      <ContentList.Provider items={collectionListItems}>
        <ContentList.Items
          renderer={(index) => {
            const collection = groups[index] as ContentCollectionGroupType;

            return (
              <CollectionItemViewer
                key={collection.id}
                Actions={
                  <RenderCollectionActions
                    assignmentItemsDueDateConfig={assignmentItemsDueDateConfig}
                    disabled={disabled}
                    item={collection}
                    setNestedDialogState={setNestedDialogState}
                    t={t}
                    onDelete={() => onDeleteCollection(collection.id)}
                    onSetDueDate={onSetDueDateForCollection}
                  />
                }
                collection={collection}
                disabled={disabled}
              />
            );
          }}
        />
      </ContentList.Provider>
      <ContentList.LargeAddBlock
        className="mt-8"
        disabled={disabled}
        subTitle={t('AssignmentCreator:AddcontentDescription')}
        title={capitalize(t('Common:AddItem'))}
        onClick={toggleContentsDialog}
      />
      {openContentsDialog ? (
        <ContentsDialog
          dialogActions={DIALOG_ACTIONS}
          dialogConfigs={{
            ...DEFAULT_DIALOG_CONFIG,
            ...(enableAssignmentRestriction ? ASSIGNMENT_RESTRICTION_DIALOG_CONFIG : {}),
          }}
          selectedIds={{
            selectedCollectionIds: collectionIds,
            selectedContentIds: contentIds,
            eventSelections,
          }}
          strings={{
            primaryButtonText: t('Common:AddItem'),
          }}
          onAddItems={onAddContent}
          onClose={toggleContentsDialog}
        />
      ) : null}
      {inViewItemForDueDate ? (
        <SetDueDateDialog
          key={
            inViewItemForDueDate.editingContent?.id ??
            inViewItemForDueDate.editingCollection?.id ??
            inViewItemForDueDate.editingSection?.id ??
            inViewItemForDueDate.editingSession?.id
          }
          assignmentItemsDueDateConfig={assignmentItemsDueDateConfig}
          inViewItemForDueDate={inViewItemForDueDate}
          nestedDialogState={nestedDialogState}
          setAssignmentItemsDueDate={setAssignmentItemsDueDate}
          setNestedDialogState={setNestedDialogState}
          onCloseDueDateDialog={onCloseDueDateDialog}
          onResetDueDate={onResetDueDate}
          onSetDueDateForContent={onSetDueDateForContent}
          onSetDueDateForSection={onSetDueDateForSection}
        />
      ) : null}
    </Stack>
  );
}

export default React.memo(ContentAndCollectionList);
